import { useCallback, useRef, useState } from 'react';
import { SidebarProvider } from '@antimatterhq/ui/components/sidebar';
import {
  getLocalStorageValue,
  prefixStorageKey,
} from '@antimatterhq/ui/utils/localStorage';
import { NetworkStatusBanner } from '@antimatterhq/ui/components/networkStatusBanner';
import { useAtomValue } from 'jotai';
import { StandaloneSidebar } from '@/components/sidebar/StandaloneSidebar';
import RedirectHandler from '@/components/auth/RedirectHandler';
import AuthHandler from '@/components/auth/AuthHandler';
import { useClientFrameNetworkErrorListener } from '@/hooks/errors/useClientFrameNetworkErrorListener';
import ClientFrame from '@/components/clientFrame/ClientFrame';
import { useDataDog } from '@/hooks/datadog/useDataDog';
import Login from '@/components/login/Login';
import { useInstanceId } from '@/contexts/InstanceIdContext';
import { networkStatusAtom } from '@/atoms/networkStatus/networkStatusAtoms';

const StandaloneNetworkStatusBanner = () => {
  const networkStatus = useAtomValue(networkStatusAtom);
  return <NetworkStatusBanner status={networkStatus} />;
};

const Index: React.FC = () => {
  const { instanceId } = useInstanceId();
  useDataDog();
  useClientFrameNetworkErrorListener();

  const [isIframeLoaded, setIsIframeLoaded] = useState(false);

  const initialOpen = useRef(
    getLocalStorageValue<boolean>(prefixStorageKey('sidebar_open')) !== false,
  );

  const handleIframeLoad = useCallback(() => {
    setIsIframeLoaded(true);
  }, []);

  const handleIframeError = useCallback(() => {
    setIsIframeLoaded(false);
  }, []);

  if (instanceId == null) {
    return <Login />;
  }

  return (
    <>
      <StandaloneNetworkStatusBanner />
      <div className="flex h-screen w-full items-center justify-center">
        <SidebarProvider defaultOpen={initialOpen.current}>
          <StandaloneSidebar disabled={!isIframeLoaded} />
          <main className="relative flex h-full w-full">
            <div className="flex h-screen w-full">
              <RedirectHandler>
                <AuthHandler>
                  <ClientFrame
                    onLoad={handleIframeLoad}
                    onError={handleIframeError}
                  />
                </AuthHandler>
              </RedirectHandler>
            </div>
          </main>
        </SidebarProvider>
      </div>
    </>
  );
};

export default Index;
